import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRecording } from '@/contexts/RecordingContext';
import { Button } from '@/components/ui/button';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { 
  Mic, 
  FileText, 
  CreditCard, 
  Settings, 
  LogOut,
  Menu,
  X,
  User,
  Laptop
} from 'lucide-react';
import { useState } from 'react';
import { useBusinessModel } from '@/hooks/useBusinessModel';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, signOut, isAdmin } = useAuth();
  const { isRecording } = useRecording();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isV2 } = useBusinessModel();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navItems = [
    { path: '/consultas', label: 'Consultas', icon: Mic },
    { path: '/templates', label: 'Templates', icon: FileText },
    { path: '/plano', label: isV2 ? 'Planos' : 'Plano & Minutos', icon: CreditCard },
    { path: '/perfil', label: 'Perfil', icon: User },
    { path: '/dispositivos', label: 'Dispositivos', icon: Laptop },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Admin', icon: Settings });
  }

  const isActive = (path: string) => location.pathname === path;

  // Get user initials for avatar
  const getUserInitials = () => {
    const name = user?.email?.split('@')[0] || '';
    return name.slice(0, 2).toUpperCase();
  };

  const getUserName = () => {
    return user?.email?.split('@')[0] || 'Usuário';
  };

  return (
    <div className={`min-h-screen flex flex-col bg-background ${isRecording ? 'pt-12' : ''}`}>
      {/* Header */}
      <header className="gradient-petrol sticky top-0 z-40 shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-[70px]">
            {/* Logo */}
            <Link to="/consultas" className="flex items-center gap-3">
              <div className="w-[42px] h-[42px] rounded-xl gradient-copper flex items-center justify-center shadow-md">
                <Mic className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight">
                Prontu<span className="text-copper-300">Áudio</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-copper-500/25 text-copper-300'
                      : 'text-white/75 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User Info & Sign Out */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right mr-2">
                <p className="text-sm font-semibold text-white leading-none">
                  {getUserName()}
                </p>
                <p className="text-xs text-white/60 mt-0.5">
                  Profissional de Saúde
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-copper-500 flex items-center justify-center text-white font-semibold text-sm cursor-pointer hover:scale-105 transition-transform">
                {getUserInitials()}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleSignOut}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-white/10 animate-fade-in">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-copper-500/25 text-copper-300'
                      : 'text-white/75 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-10 h-10 rounded-xl bg-copper-500 flex items-center justify-center text-white font-semibold">
                    {getUserInitials()}
                  </div>
                  <div>
                    <p className="font-medium text-white">{getUserName()}</p>
                    <p className="text-sm text-white/60">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/75 hover:text-white hover:bg-white/10 rounded-xl transition-colors mt-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sair</span>
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        Desenvolvido por <span className="font-medium text-copper-500">Inspira Criativa</span>
      </footer>

      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
}
